import { useMemo } from 'react'

import { useIntl } from 'react-intl'
import { styled } from 'styled-components'

import { isZeroQuotePlaceholder } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedCommonListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import {
  CurrencyPlaceholder,
  AnalyticsEntityType,
  AnalyticsSource,
  LinkAsButton,
  CurrencyStringDollars,
  CurrencyStringCents,
  FormattedMessage,
  Tag,
  TagType,
  Banner,
} from '@b-stock/fe-core/bstock-react'
import { Breakpoints, Colors, Typography } from '@b-stock/fe-core/design-system'
import type { Shipping } from '@b-stock/listing-api-client'

import type { CardOrderItem } from '@/buyer-portal/components/CardOrder'
import { CardOrder } from '@/buyer-portal/components/CardOrder'
import type { CalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import {
  suppressChargesTotal,
  useSuppressShippingLine,
} from '@/buyer-portal/components/features/listing/hooks/useSuppressShippingLine'
import {
  getQuoteShipmentType,
  UnavailableValue,
} from '@/buyer-portal/components/features/listing/hooks/useTotalPaymentInfo'
import { getZeroQuoteBannerMessageId } from '@/buyer-portal/components/features/listing/ListingActionColumn/getZeroQuoteBannerMessageId'
import { isFixedShipmentType } from '@/buyer-portal/helpers/shipping'
import { useIsServiceAccountOnly } from '@/buyer-portal/helpers/useIsServiceAccountOnly'
import { isWireOnlyCurrency } from '@/buyer-portal/helpers/wireOnlyCurrency'
import { calculateShippingCostBreakdown } from '@/helpers/calculateShippingCostBreakdown'
import { useAnalytics } from '@/helpers/telemetry/SegmentAnalytics'

import { AvailableMethods } from './AvailableMethods'

const ns = 'Listing.actionColumn.payment'

const Body = styled.div`
  background: ${Colors.Semantic.Neutral.light_grey};
  border-radius: 0.5rem;
  padding: 1.5rem 2rem;

  @media ${Breakpoints.max.medium} {
    padding: 1.5rem 2rem;
    background: ${Colors.Semantic.Neutral.light_grey};
    border-radius: 0 0 0.5rem 0.5rem;
    &&& {
      ul > li:first-child > div {
        ${Typography.Subtitle2};
      }
    }
  }
  width: 100%;
  max-width: calc(100vw - 2rem);
`

const AdditionalCharges = styled.div`
  margin: 0rem -2rem 0rem -2rem;
  padding: 0rem 2rem 0rem 2rem;

  @media ${Breakpoints.max.medium} {
    ${Typography.Subtitle2};
    margin: 0rem -1rem 0rem -1rem;
    padding: 0rem 1rem 0rem 1rem;
    border-bottom: none;
  }
`

const NoRatesBanner = styled(Banner)`
  margin-bottom: 1.5rem;
`

const ShippingFeeNote = styled.div`
  ${Typography.Body3}
  color: ${Colors.Semantic.Neutral.dark_grey};

  @media ${Breakpoints.max.medium} {
    ${Typography.Caption}
`
const AvailableMethodsBody = styled.div`
  padding-top: 1.5rem;
  @media ${Breakpoints.max.medium} {
    display: none;
  }
`
export const Payment = ({
  additionalCharges,
  basePrice,
  buyNowPrice,
  currency = 'USD',
  editDeliveryMethod,
  hideAdditionalCharges,
  isWithinBuyNowWindow,
  loading = false,
  selectedQuote,
  shipping,
  currentListing,
}: {
  additionalCharges: CalculatedAdditionalCharges
  /**
   * basePrice in dollars
   */
  basePrice: number
  /**
   * buyNowPrice in dollars
   */
  buyNowPrice?: number | null
  currency?: string
  editDeliveryMethod: () => void
  hideAdditionalCharges?: boolean
  isWithinBuyNowWindow?: boolean
  loading?: boolean
  selectedQuote?: OrchestratedQuote | null
  shipping?: Shipping
  currentListing?: OrchestratedCommonListingDetails
}) => {
  const { formatMessage } = useIntl()
  const { trackButtonClicked } = useAnalytics()
  const isServiceAccountOnly = useIsServiceAccountOnly()
  const isZeroQuoteValue = isZeroQuotePlaceholder(selectedQuote)
  // The same rule the bid card and the confirmation modals apply, so the two
  // breakdowns on this screen cannot disagree about the same lot.
  const suppressShippingLine = useSuppressShippingLine(
    currentListing,
    selectedQuote
  )
  const suppressTotal = suppressChargesTotal(
    suppressShippingLine,
    additionalCharges.total
  )

  const {
    estimatedShippingAmount,
    liftgateDeliveryFee,
    residentialDeliveryFee,
  } = calculateShippingCostBreakdown(selectedQuote)

  const shipmentType = getQuoteShipmentType(selectedQuote, shipping)
  const isFixedShipping = isFixedShipmentType(shipmentType)

  const totalPaymentInfo: CardOrderItem[] = useMemo(
    () => [
      {
        title: formatMessage({ id: `${ns}.additionalCharges` }),
        value:
          loading || isZeroQuoteValue ? (
            <CurrencyPlaceholder currency={currency} />
          ) : suppressTotal ? (
            <UnavailableValue>—</UnavailableValue>
          ) : (
            <CurrencyStringCents
              value={additionalCharges.total}
              currency={currency}
            />
          ),
        isValueWarning: loading || isZeroQuoteValue,
        description: (
          <FormattedMessage
            id={
              isFixedShipping
                ? `${ns}.estimateFixedShipping`
                : `${ns}.estimateDynamicShipping`
            }
            values={{
              estimate: (
                <CurrencyStringDollars
                  value={
                    isWithinBuyNowWindow && buyNowPrice
                      ? buyNowPrice
                      : basePrice
                  }
                  currency={currency}
                  omitDecimal
                />
              ),
            }}
          />
        ),
        children: [
          {
            title: formatMessage({ id: 'TotalPaymentInfo.bstockFee' }),
            value: (
              <CurrencyStringCents
                value={additionalCharges.bstockFee}
                currency={currency}
              />
            ),
          },
          {
            title: formatMessage({ id: `${ns}.shipping` }),
            tag: isFixedShipping ? (
              <Tag type={TagType.info}>
                <FormattedMessage
                  id={`TotalPaymentInfo.shippingTags.${shipmentType}`}
                />
              </Tag>
            ) : undefined,
            value: suppressShippingLine ? (
              <UnavailableValue>—</UnavailableValue>
            ) : selectedQuote && !isZeroQuoteValue ? (
              <CurrencyStringCents
                value={selectedQuote.totalPrice}
                currency={currency}
              />
            ) : (
              <CurrencyPlaceholder currency={currency} />
            ),
            // Not a warning: there is no figure to wait for, by design.
            isValueWarning:
              !suppressShippingLine && !(selectedQuote && !isZeroQuoteValue),
            description:
              isFixedShipping || suppressShippingLine
                ? undefined
                : formatMessage({
                    id: 'TotalPaymentInfo.shippingTotalDescription',
                  }),
            // With no amount to assert there is nothing to break down.
            children:
              !suppressShippingLine &&
              selectedQuote &&
              !isFixedShipping &&
              !isZeroQuoteValue
                ? [
                    {
                      title: formatMessage({ id: `${ns}.shippingFee` }),
                      description: formatMessage({
                        id: `${ns}.shippingFeeDescription`,
                      }),
                      value: (
                        <CurrencyStringCents
                          value={estimatedShippingAmount}
                          currency={currency}
                        />
                      ),
                    },
                    {
                      title: formatMessage({
                        id: 'TotalPaymentInfo.liftgateFee',
                      }),
                      description: formatMessage({
                        id: `${ns}.liftgateFeeDescription`,
                      }),
                      value: (
                        <CurrencyStringCents
                          value={liftgateDeliveryFee || 0}
                          currency={currency}
                        />
                      ),
                    },
                    {
                      title: formatMessage({
                        id: 'TotalPaymentInfo.residentialFee',
                      }),
                      description: formatMessage({
                        id: `${ns}.residentialFeeDescription`,
                      }),
                      value: (
                        <CurrencyStringCents
                          value={residentialDeliveryFee || 0}
                          currency={currency}
                        />
                      ),
                    },
                  ]
                : [],
          },
        ],
      },
    ],
    [
      additionalCharges.bstockFee,
      additionalCharges.total,
      basePrice,
      buyNowPrice,
      currency,
      estimatedShippingAmount,
      isFixedShipping,
      formatMessage,
      isWithinBuyNowWindow,
      isZeroQuoteValue,
      liftgateDeliveryFee,
      loading,
      residentialDeliveryFee,
      selectedQuote,
      shipmentType,
      suppressShippingLine,
      suppressTotal,
    ]
  )

  const showShippingFeeNote =
    !loading && additionalCharges.isWithoutShipping && !isServiceAccountOnly
  // hideAdditionalCharges only suppresses the duplicate total (already shown
  // by BidActionCard's TlcBreakdown) -- the no-rates warning and the
  // shipping-not-included note below it are not shown anywhere else.
  const hasAdditionalChargesContent =
    !hideAdditionalCharges || isZeroQuoteValue || showShippingFeeNote

  return (
    <>
      {/* Skip the wrapper entirely rather than rendering it around empty
          content -- otherwise Body's grey background and padding leave a
          blank card where the "hidden" duplicate total used to be. */}
      {hasAdditionalChargesContent && (
        <Body data-testid="action-column-payment">
          <AdditionalCharges>
            {isZeroQuoteValue && (
              <NoRatesBanner type="warning">
                <FormattedMessage
                  id={getZeroQuoteBannerMessageId(selectedQuote)}
                />
              </NoRatesBanner>
            )}
            {hideAdditionalCharges ? null : (
              <div
                onClick={(event) => {
                  // Check if click was on an arrow button by looking for the button element
                  const target = event.target as HTMLElement
                  const button = target.closest('button')
                  if (!button || !currentListing) return

                  // Get the text content to determine which section was toggled
                  const cardOrderItem = button.closest('li')
                  if (!cardOrderItem) return

                  const itemTitle =
                    cardOrderItem.querySelector('span')?.textContent
                  if (!itemTitle) return

                  let buttonName = ''
                  if (
                    itemTitle.includes(
                      formatMessage({ id: `${ns}.additionalCharges` })
                    )
                  ) {
                    buttonName = 'shipping_and_other_charges'
                  } else if (
                    itemTitle.includes(formatMessage({ id: `${ns}.shipping` }))
                  ) {
                    buttonName = 'shipping_charges'
                  }

                  if (buttonName) {
                    trackButtonClicked({
                      screenName: 'listing_detail_page',
                      source: AnalyticsSource.HOME_PORTAL,
                      entityType: AnalyticsEntityType.BUYER,
                      listing_id: currentListing.lot._id,
                      seller_account_id: currentListing.accountId,
                      storefront_name: currentListing.seller.storefront?.name,
                      seller_display_name:
                        currentListing.seller.account.displayName || '',
                      buttonName,
                    })
                  }
                }}
              >
                <CardOrder data={totalPaymentInfo} level={1} />
              </div>
            )}
            {showShippingFeeNote ? (
              <ShippingFeeNote>
                <FormattedMessage
                  id={`${ns}.shipping.fee.noteWithLink`}
                  values={{
                    link: (
                      <LinkAsButton onClick={editDeliveryMethod}>
                        <FormattedMessage id={`${ns}.shipping.fee.noteLink`} />
                      </LinkAsButton>
                    ),
                  }}
                />
              </ShippingFeeNote>
            ) : null}
          </AdditionalCharges>
        </Body>
      )}
      {/* AvailableMethods already returns null for wire-only currencies, but
          skip the wrapper entirely rather than rendering it around empty
          content — otherwise AvailableMethodsBody's padding-top leaves a
          blank gap where the "hidden" card used to be. */}
      {!isWireOnlyCurrency(currency) && (
        <AvailableMethodsBody data-testid="available-methods-body">
          <AvailableMethods
            isCreditCardEnabled={currentListing?.isCreditCardEnabled}
            currency={currency}
            listing={currentListing}
          />
        </AvailableMethodsBody>
      )}
    </>
  )
}
